import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Badge, Box, Button, createStyles, Group, Menu, Textarea} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface ChatEntryPointProps {
  onClick: () => void;
  variant: 'chat';
}

interface HomepageEntryPointProps {
  onStartFoundryChat: (description: string) => void;
  variant: 'homepage';
}

type CreateAgentFoundryEntryPointProps = ChatEntryPointProps | HomepageEntryPointProps;

const useChatButtonStyles = createStyles((theme) => ({
  chatButton: {
    fontWeight: theme.other.fontWeights.normal,
  },
  betaBadge: {
    color: theme.colors.blue[7],
  },
}));

const useHomepageStyles = createStyles((theme) => ({
  dropdown: {
    backgroundColor: 'transparent',
    border: 0,
    boxShadow: 'none',
    maxWidth: `calc(100vw - ${theme.spacing.xl} - ${theme.spacing.xl})`,
    padding: 0,
  },
  descriptionBox: {
    position: 'relative',
  },
  descriptionInput: {
    borderRadius: theme.radius.xl,
    boxShadow: theme.shadows.sm,
    fontSize: theme.fontSizes.xl,
    lineHeight: 1.4,
    padding: theme.spacing.xl,
    paddingRight: 72,
  },
  descriptionSubmit: {
    position: 'absolute',
    bottom: theme.spacing.lg,
    right: theme.spacing.lg,
  },
}));

export default function CreateAgentFoundryEntryPoint(props: CreateAgentFoundryEntryPointProps): JSX.Element {
  return props.variant === 'chat' ? (
    <ChatEntryPoint onClick={props.onClick} />
  ) : (
    <HomepageEntryPoint onStartFoundryChat={props.onStartFoundryChat} />
  );
}

function ChatEntryPoint({onClick}: {onClick: () => void}): JSX.Element {
  const {classes} = useChatButtonStyles();
  return (
    <Button
      className={classes.chatButton}
      leftIcon={<BenchlingIcon icon="gears-outline" />}
      onClick={onClick}
      radius="xl"
      size="sm"
      variant="secondary"
      {...getTestProps({component: 'CreateAgentFoundryEntryPoint', element: 'chatButton'})}
    >
      <Group spacing={6}>
        Create a foundry
        <Badge className={classes.betaBadge} size="xs" variant="light">
          Beta
        </Badge>
      </Group>
    </Button>
  );
}

function HomepageEntryPoint({onStartFoundryChat}: Omit<HomepageEntryPointProps, 'variant'>): JSX.Element {
  const [menuOpened, setMenuOpened] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const {classes} = useHomepageStyles();

  function handleDescriptionSubmit(): void {
    const description = descriptionDraft.trim();
    if (!description) {
      return;
    }
    setMenuOpened(false);
    setDescriptionDraft('');
    onStartFoundryChat(description);
  }

  return (
    <Menu
      opened={menuOpened}
      onChange={setMenuOpened}
      position="bottom-end"
      shadow="md"
      width={800}
      withinPortal={true}
    >
      <Menu.Target>
        <Button
          leftIcon={<BenchlingIcon icon="plus" />}
          rightIcon={<BenchlingIcon icon="chevron-down" />}
          size="xs"
          {...getTestProps({component: 'CreateAgentFoundryEntryPoint', element: 'homepageButton'})}
        >
          New foundry
        </Button>
      </Menu.Target>
      <Menu.Dropdown className={classes.dropdown}>
        <Box className={classes.descriptionBox}>
          <Textarea
            aria-label="Describe what it should do"
            autosize={true}
            classNames={{input: classes.descriptionInput}}
            maxRows={12}
            minRows={6}
            placeholder="Describe what it should do"
            value={descriptionDraft}
            onChange={(event) => {
              setDescriptionDraft(event.currentTarget.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleDescriptionSubmit();
              }
            }}
          />
          <ActionIcon
            aria-label="Continue with description"
            className={classes.descriptionSubmit}
            color="blue"
            disabled={!descriptionDraft.trim()}
            onClick={handleDescriptionSubmit}
            radius="xl"
            size="xl"
            variant="filled"
          >
            <BenchlingIcon icon="arrow-up" />
          </ActionIcon>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}
